import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

type MobileSidebarContextValue = {
  // Navigation dropdown state
  navigationOpen: boolean
  setNavigationOpen: (open: boolean) => void

  // Settings drawer state
  settingsOpen: boolean
  setSettingsOpen: (open: boolean) => void

  // Close all - called when navigating
  closeAll: () => void
}

const MobileSidebarContext = createContext<MobileSidebarContextValue | null>(
  null,
)

type MobileSidebarProviderProps = {
  children: ReactNode
}

export function MobileSidebarProvider({
  children,
}: MobileSidebarProviderProps): JSX.Element {
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const closeAll = useCallback(() => {
    setNavigationOpen(false)
    setSettingsOpen(false)
  }, [])

  const value: MobileSidebarContextValue = useMemo(
    () => ({
      navigationOpen,
      setNavigationOpen,
      settingsOpen,
      setSettingsOpen,
      closeAll,
    }),
    [navigationOpen, settingsOpen, closeAll],
  )

  return (
    <MobileSidebarContext.Provider value={value}>
      {children}
    </MobileSidebarContext.Provider>
  )
}

export function useMobileSidebar(): MobileSidebarContextValue {
  const context = useContext(MobileSidebarContext)
  if (!context) {
    throw new Error(
      'useMobileSidebar must be used within a MobileSidebarProvider',
    )
  }
  return context
}

/**
 * Safe version of useMobileSidebar that returns null when not in a provider.
 * Useful for components that are used in both mobile and desktop contexts.
 */
export function useMobileSidebarSafe(): MobileSidebarContextValue | null {
  return useContext(MobileSidebarContext)
}
